// IE Hub - Share Success Page
// Displays success message after sharing budget with companies

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  Backdrop,
  useMediaQuery,
  Slide,
  useTheme,
} from "@/ui";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import BottomTabBar from "@/components/BottomTabBar";
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon,
  MailOutline as MailOutlineIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from "@mui/icons-material";
import AddCompanyDialog from "@/components/AddCompanyDialog";
import { LenderService } from "@/shared/api/services";
import { findMasterLender, LenderParentRecord } from "@/shared/api/services/lenderService";
import {
  ApiResponse,
  CalculatedTotals,
  CustomerDeclarationEntry,
  CustomerPreferencesEntry,
  CustomerSharingResponse,
  DigitalLender,
  ServiceResponse,
} from "@/shared/api/types";
import {
  customerEndpoints,
  declarationEndpoints,
  sharePageEndpoints,
} from "@/shared/api/endpoints";
import femaCapture from "@/shared/fema/capture";
import { httpGet, httpPut } from "@/shared/api/http";
import { trackMetaEvent } from "@/shared/tracking/metaTracking";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { Company } from "@/components/AddCompanyForm";
import { SharePageService } from "@/shared/api/services/sharePageService";

const PAYPLAN_ACCENT = "#d4e837";
const PAYPLAN_CARD_BG = "#003d40";
const PAYPLAN_PHONE = "0800 280 2816";
const PAYPLAN_HOURS = "Mon–Fri 8am–8pm · Sat 9am–3pm";

type PayPlanCardVariant = "promo" | "text" | "email" | "whatsapp";

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

const ShareSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [benefitsAmount, setBenefitsAmount] = useState("");
  const [balanceAmount, setBalanceAmount] = useState<number>(0);
  const [sharedCompanies, setSharedCompanies] = useState<string[]>([]);
  const [isShared, setIsShared] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [companies, setCompanies] = useState<Company[]>([]);
  const [payPlanVariants, setPayPlanVariants] = useState<PayPlanCardVariant[]>(
    [],
  );
  const [customerMobileNumber, setCustomerMobileNumber] = useState("");
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  const [summary, setSummary] = useState<CalculatedTotals>({
    customerId: 0,
    totalIncome: 0,
    totalAssets: 0,
    totalOutgoingsFixed: 0,
    totalOutgoingsFlexible: 0,
    totalOwingOnPriorityDebt: 0,
    totalRepayOnPriorityDebt: 0,
    totalOwingOnNonPriorityDebt: 0,
    totalRepayOnNonPriorityDebt: 0,
    pendingPaymentOffer: 0,
    balanceAmount: 0,
    totalRepaymentDebts: 0,
    totalOwingOnDebts: 0,
  });

  const [freepostCreditorNamesData, setFreepostCreditorNamesData] = useState(
    []
  );
  const [declarationData, setDeclarationData] = useState<
    CustomerDeclarationEntry[]
  >([]);
  const [ieMstrLenderData, setIeMstrLenderData] = useState<
    LenderParentRecord[]
  >([
    {
      id: 0,
      parentCompany: "",
      description: "",
      showCompanyList: false,
      referralCompanies: false,
      customerPaymentOffer: false,
      addToDebtList: false,
      referenceNumberFormat: "",
      childCompany: [],
      raw: { id: 0, lender: "" },
      childrenRaw: [],
    },
  ]);

  // Visible label only. The stored share list still uses the short company name.
  const companyDisplayName = (name: string): string =>
    findMasterLender(ieMstrLenderData, name)?.description?.trim() || name;

  const [digitalLenders, setDigitalLenders] = useState<DigitalLender[]>([
    {
      schemaName: "",
      creditor: "",
      subDomain: "",
      lender: "",
      referenceNumberFormat: "",
      customerPaymentOffer: "",
      showCompanyList: "",
      addToDebtList: "",
      parentCompany: "",
      creditorAddress: "",
      isCreditorExistsWithIeHub: "",
    },
  ]);

  // Legacy parity: fire Meta Pixel + CAPI event on success screen
  useEffect(() => {
    void trackMetaEvent("Purchase", "Purchase");
  }, []);

  useEffect(() => {
    const fetchCompanies = async (): Promise<Company[]> => {
      const resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        return [];
      }

      try {
        const coerceNumericId = (value: unknown): number | null => {
          if (typeof value === "number" && Number.isFinite(value)) return value;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
          }
          return null;
        };

        const isSharedRow = (row: any): boolean => {
          const submit =
            typeof row?.submitDate === "string" ? row.submitDate.trim() : "";
          const lastShared =
            typeof row?.lastSharedDate === "string"
              ? row.lastSharedDate.trim()
              : "";
          return Boolean(submit || lastShared);
        };

        /**
         * IMPORTANT (legacy parity):
         * - Keep ALL rows that were already shared (submitDate/lastSharedDate present),
         *   because history must remain visible (and reference edits after sharing create a new row).
         * - Only dedupe *pending/unshared* duplicates (no submitDate/lastSharedDate), since these
         *   are the "company shows twice" bug scenario.
         */
        const dedupeSharingRows = (rows: any[]): any[] => {
          const bestPendingByKey = new Map<string, any>();

          const keyForRow = (row: any): string => {
            const debtId = coerceNumericId(row?.debtId);
            const id = coerceNumericId(row?.id);
            if (typeof debtId === "number") return `debt:${debtId}`;
            if (typeof id === "number") return `id:${id}`;
            return `fallback:${String(row?.lenderName ?? row?.companyName ?? "")}|${String(
              row?.accountNumber ?? row?.referenceNumber ?? ""
            )}`;
          };

          // Pass 1: choose the "best" pending row per key (latest lastUpdateDate)
          for (const row of rows) {
            if (isSharedRow(row)) continue;
            const key = keyForRow(row);
            const prev = bestPendingByKey.get(key);
            const prevTs = prev?.lastUpdateDate
              ? Date.parse(prev.lastUpdateDate)
              : NaN;
            const nextTs = row?.lastUpdateDate
              ? Date.parse(row.lastUpdateDate)
              : NaN;
            if (
              !prev ||
              (Number.isFinite(nextTs) &&
                (!Number.isFinite(prevTs) || nextTs >= prevTs))
            ) {
              bestPendingByKey.set(key, row);
            }
          }

          // Pass 2: rebuild list preserving original order:
          // - include all shared rows
          // - include only the chosen pending row per key
          const emittedPendingKeys = new Set<string>();
          const out: any[] = [];
          for (const row of rows) {
            if (isSharedRow(row)) {
              out.push(row);
              continue;
            }
            const key = keyForRow(row);
            if (emittedPendingKeys.has(key)) continue;
            if (bestPendingByKey.get(key) === row) {
              out.push(row);
              emittedPendingKeys.add(key);
            }
          }
          return out;
        };

        const digitalDataRaw: any =
          await SharePageService.getCustomerSharing(resolvedCustomerId);
        const digitalData: any[] = Array.isArray(digitalDataRaw)
          ? dedupeSharingRows(digitalDataRaw)
          : [];

        const customerData = JSON.parse(
          localStorage.getItem("customerData") as string
        );

        const freePostDataRaw: any = await SharePageService.getFreePostData(
          customerData.customerId
        );
        const freePostData: any[] = Array.isArray(freePostDataRaw)
          ? dedupeSharingRows(freePostDataRaw)
          : [];

        const data = [
          ...(await Promise.all(
            digitalData.map(async (company: any) => {
              if (!company.lenderName) {
                return {};
              }

              // const correspondingDebt = debts.find((entry: any) => {
              //   return entry.id == company.debtId;
              // });

              let type = "all";

              const obj: Company = {
                id: company.id,
                name: company.lenderName,
                reference: company.accountNumber,
                type: type as Company["type"],
                address: company.address || "",
                documents: company.documents || [],
                selectedDocuments: [],
                lastShared: company.lastShared || "",
                raw: company,
              };

              return obj;
            })
          )),

          ...freePostData.map((company: any) => {
            let type = "all";

            const shareDate = company.lastSharedDate || company.submitDate;

            let obj: Company = {
              id: company.id,
              name: company.companyName,
              reference: company.referenceNumber,
              type: type as Company["type"],
              address: company.address || "",
              documents: company.documents || [],
              selectedDocuments: [],
              lastShared: company.lastShared || "",
              raw: company,
              isFp: true,
            };

            return obj;
          }),
        ];

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return [];
      }
    };

    fetchCompanies().then((companiesArray) => {
      setCompanies(companiesArray);
    });
  }, []);

  // GET Digital Creditor List Names
  useEffect(() => {
    async function getCreditorNames(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listDigitalLenders
        );

        setDigitalLenders((response.data as DigitalLender[]).filter((d) => !!d.creditor));
      } catch (error) {
        femaCapture("fetching_creditor_list_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getCreditorNames();
  }, []);

  // GET lender data
  useEffect(() => {
    async function getCustomerShareData() {
      const { data } = await LenderService.fetchLenders({
        skipErrorCapture: true,
      });

      setIeMstrLenderData(data);

      return;
    }

    getCustomerShareData();
  }, []);

  // GET balance amount (Calculated Totals) - required for showing Payment Offer section in Add Company flow
  useEffect(() => {
    const customerDataStr = localStorage.getItem("customerData");
    if (!customerDataStr) return;

    const customerData = JSON.parse(customerDataStr) as {
      customerId?: unknown;
    };
    const customerIdRaw = customerData?.customerId;
    const customerId =
      typeof customerIdRaw === "number"
        ? customerIdRaw
        : typeof customerIdRaw === "string" && customerIdRaw.trim().length > 0
          ? Number(customerIdRaw)
          : null;

    if (typeof customerId !== "number" || !Number.isFinite(customerId)) return;

    (async () => {
      try {
        const response = await httpGet<any>(
          customerEndpoints.getCalculatedTotals(customerId)
        );

        const raw = response?.data;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

        const nextBalanceRaw = arr?.[0]?.balanceAmount;
        const nextBalance =
          typeof nextBalanceRaw === "number"
            ? nextBalanceRaw
            : typeof nextBalanceRaw === "string" &&
                nextBalanceRaw.trim().length > 0
              ? Number(nextBalanceRaw)
              : 0;

        setBalanceAmount(Number.isFinite(nextBalance) ? nextBalance : 0);
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    })();
  }, []);

  // GET declaration data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    async function getDeclarationData() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclaration(customerData.customerId)
        );

        const data: any = response?.data;

        setDeclarationData(data);
      } catch (error) {
        femaCapture("fetching_declaration_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationData();
  }, []);

  useEffect(() => {
    async function getFreepostCreditorNames(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listFreepostLenders
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_freepost_creditor_list_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getFreepostCreditorNames().then((data: any) => {
      setFreepostCreditorNamesData(data);
    });
  }, []);

  useEffect(() => {
    setSharedCompanies(
      localStorage
        .getItem("companyList")
        ?.split(";")
        .filter(function (el) {
          return el.length != 0;
        }) || []
    );
  }, []);

  const hasFetchedSummary = useRef(false);

  // GET summary
  useEffect(() => {
    if (hasFetchedSummary.current) return;
    hasFetchedSummary.current = true;

    async function getSummary(): Promise<ServiceResponse<any[]>> {
      try {
        const resolvedCustomerId: string | number | null =
          await getCustomerId();

        const response = await httpGet<ApiResponse>(
          customerEndpoints.getCalculatedTotals(Number(resolvedCustomerId))
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_calculated_totals_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getSummary().then((res: any) => {
      setSummary(res[0]);
    });
  }, []);

  useEffect(() => {
    const isShared = localStorage.getItem("alreadyShared") == "Y";
    setIsShared(isShared);
  }, []);

  const hasFetchedPreferences = useRef(false);

  // PayPlan promo card — both conditions required:
  // 1) Customer has not shared with PayPlan (not in this session companyList)
  // 2) ie-customer-sharing has lenderName "PayPlan" and lastSharedDate is missing/empty
  // Call/WhatsApp/Email cards when Customer has shared with PayPlan (in this session companyList) and PayPlan exists in ie-customer-sharing
  // (with or without lastSharedDate), then use customer-preferences flags
  useEffect(() => {
    if (hasFetchedPreferences.current) return;
    hasFetchedPreferences.current = true;

    const isPayPlanName = (name: string): boolean => {
      const normalized = name.trim().toLowerCase().replace(/\s+/g, "");
      return normalized === "payplan";
    };

    const hasLastSharedDate = (value: unknown): boolean =>
      typeof value === "string" && value.trim().length > 0;

    async function getCustomerPreferencesData() {
      try {
        const rawCustomerData = localStorage.getItem("customerData");
        const customerData = rawCustomerData
          ? (JSON.parse(rawCustomerData) as Record<string, unknown>)
          : null;

        const mobile =
          typeof customerData?.mobileNumber === "string"
            ? customerData.mobileNumber
            : "";
        setCustomerMobileNumber(mobile);

        const sharedList =
          localStorage
            .getItem("companyList")
            ?.split(";")
            .map((name) => name.trim())
            .filter((name) => name.length > 0) ?? [];

        // Condition 1: has not shared with PayPlan (this session shared list)
        const hasSharedWithPayPlan = sharedList.some(isPayPlanName);

        const customerId =
          customerData?.customerId ?? (await getCustomerId());

        if (
          customerId === null ||
          customerId === undefined ||
          customerId === ""
        ) {
          setPayPlanVariants([]);
          setPreferencesLoaded(true);
          return;
        }

        // Condition 2: PayPlan on ie-customer-sharing (with or without lastSharedDate)
        let payPlanOnShareListWithoutLastSharedDate = false;
        let payPlanHasLastSharedDate = false;
        let payPlanExistsOnShareList = false;
        try {
          const sharingRows = await SharePageService.getCustomerSharing(
            typeof customerId === "number" || typeof customerId === "string"
              ? customerId
              : String(customerId),
          );
          const rows = Array.isArray(sharingRows) ? sharingRows : [];
          const payPlanRows = rows.filter(
            (row) =>
              isPayPlanName(String(row?.lenderName || "")) &&
              row?.isDeleted != "Y",
          );

          payPlanExistsOnShareList = payPlanRows.length > 0;
          payPlanHasLastSharedDate = payPlanRows.some((row) =>
            hasLastSharedDate(row?.lastSharedDate),
          );
          payPlanOnShareListWithoutLastSharedDate =
            payPlanExistsOnShareList &&
            payPlanRows.some((row) => !hasLastSharedDate(row?.lastSharedDate));
        } catch (sharingError) {
          femaCapture(
            "fetching_customer_sharing_payplan_share_success_error",
            sharingError,
            {
              category: "api",
              severity: "error",
              skipSanitization: false,
              user: {},
            },
          );
        }

        // Promo: not shared with PayPlan AND PayPlan on share list without lastSharedDate
        if (
          !hasSharedWithPayPlan &&
          payPlanOnShareListWithoutLastSharedDate &&
          !payPlanHasLastSharedDate
        ) {
          setPayPlanVariants(["promo"]);
          setPreferencesLoaded(true);
          return;
        }

        // Call / WhatsApp / Email: shared with PayPlan this session (companyList)
        // AND PayPlan exists in ie-customer-sharing (with or without lastSharedDate)
        if (!hasSharedWithPayPlan || !payPlanExistsOnShareList) {
          setPayPlanVariants([]);
          setPreferencesLoaded(true);
          return;
        }

        const prefsResp = await httpGet<any>(
          customerEndpoints.getCustomerPreferences(String(customerId)),
        );

        const body = prefsResp?.data;
        const rows: CustomerPreferencesEntry[] = Array.isArray(body)
          ? body
          : Array.isArray(body?.data)
            ? body.data
            : [];

        const payPlanPref = rows.find((entry) =>
          isPayPlanName(String(entry.companyName || "")),
        );

        const variants: PayPlanCardVariant[] = [];
        if (payPlanPref) {
          const textMeNdl =
            String(payPlanPref.textMeNdl || "").toUpperCase() === "Y";
          const emailMeNdl =
            String(payPlanPref.emailMeNdl || "").toUpperCase() === "Y";
          const whatsAppRaw = String(payPlanPref.whatsAppConsent ?? "").trim();
          const whatsAppConsentY = whatsAppRaw.toUpperCase() === "Y";
          const whatsAppConsentOff =
            whatsAppRaw === "" || whatsAppRaw.toUpperCase() === "N";

          // Call/text card: textMeNdl Y + whatsAppConsent N/empty/null
          if (textMeNdl && whatsAppConsentOff) {
            variants.push("text");
          }
          if (emailMeNdl) {
            variants.push("email");
          }
          // WhatsApp card: textMeNdl Y + whatsAppConsent Y
          if (textMeNdl && whatsAppConsentY) {
            variants.push("whatsapp");
          }
        }
        setPayPlanVariants(variants);
      } catch (error) {
        setPayPlanVariants([]);
        femaCapture("fetching_customer_preferences_share_success_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      } finally {
        setPreferencesLoaded(true);
      }
    }

    void getCustomerPreferencesData();
  }, []);

  const handleShareWithCompany = () => {
    setAddCompanyOpen(true);
  };

  const handleSearchCompanies = () => {
    navigate("/freepost");
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleStartBenefitsCheck = () => {
    navigate("/benefits");
  };

  useEffect(() => {
    setBenefitsAmount(localStorage.getItem("BENEFITS_AMOUNT") || "0");
  }, []);

  const handleGetPayPlanAdvice = async () => {
    try {
      const customerId = await getCustomerId();
      if (
        customerId !== null &&
        customerId !== undefined &&
        `${customerId}` !== ""
      ) {
        const sharingRows = await SharePageService.getCustomerSharing(
          customerId,
        );
        const rows = Array.isArray(sharingRows) ? sharingRows : [];
        const isPayPlanName = (name: string): boolean => {
          const normalized = name.trim().toLowerCase().replace(/\s+/g, "");
          return normalized === "payplan";
        };

        const payPlanRows = rows.filter(
          (row) =>
            isPayPlanName(String(row?.lenderName || "")) &&
            row?.isDeleted != "Y",
        );

        await Promise.all(
          payPlanRows
            .filter((row) => row.shareNow !== "Y")
            .map(async (row) => {
              const payload = {
                ...row,
                shareNow: "Y",
              };
              delete (payload as { editable?: unknown }).editable;
              await httpPut<CustomerSharingResponse>(
                sharePageEndpoints.createCustomerSharing(),
                payload,
              );
            }),
        );
      }
    } catch (error) {
      femaCapture("payplan_promo_share_now_update_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });
    } finally {
      navigate("/share");
    }
  };

  const renderPayPlanLogo = (centered = false) => (
    <Box
      data-testid="pages-private-from-dashboard-share-success-page-container-payplan-logo"
      component="img"
      src="/assets/payplan-logo.svg"
      alt="PayPlan"
      sx={{
        width: centered ? 56 : 48,
        height: centered ? 56 : 48,
        borderRadius: "12px",
        bgcolor: "#ffffff",
        objectFit: "contain",
        p: 0.75,
        flexShrink: 0,
      }}
    />
  );

  const renderPayPlanPromoCard = () => (
    <Paper
      data-testid="pages-private-from-dashboard-share-success-page-container-payplan-promo"
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        bgcolor: PAYPLAN_CARD_BG,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        {renderPayPlanLogo()}
        <Typography
          data-testid="pages-private-from-dashboard-share-success-page-text-payplan-promo-title"
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#ffffff",
            lineHeight: 1.3,
          }}
        >
          You could benefit from PayPlan&apos;s{" "}
          <Box component="span" sx={{ color: PAYPLAN_ACCENT }}>
            free debt advice
          </Box>
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {[
          "Take the first step to getting back in control of your finances",
          "Instant support tailored to your needs",
          "Available via WhatsApp and over the phone",
        ].map((text) => (
          <Box
            key={text}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}
          >
            <CheckCircleIcon
              sx={{ color: PAYPLAN_ACCENT, fontSize: 22, mt: "1px" }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                color: "#e8ecec",
                lineHeight: 1.4,
              }}
            >
              {text}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button
        data-testid="pages-private-from-dashboard-share-success-page-btn-get-payplan-advice"
        fullWidth
        onClick={handleGetPayPlanAdvice}
        sx={{
          bgcolor: PAYPLAN_ACCENT,
          color: "#00243e",
          borderRadius: "999px",
          py: 1.25,
          fontSize: "16px",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": {
            bgcolor: "#c2d123",
          },
        }}
      >
        Get debt advice from PayPlan
      </Button>
    </Paper>
  );

  const renderPayPlanInfoRows = (
    rows: Array<{ icon: React.ReactNode; text: React.ReactNode }>,
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {rows.map((row, index) => (
        <Box
          key={index}
          sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
        >
          <Box sx={{ color: PAYPLAN_ACCENT, mt: "2px", display: "flex" }}>
            {row.icon}
          </Box>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#ffffff",
              lineHeight: 1.45,
            }}
          >
            {row.text}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderPayPlanTextCard = () => (
    <Paper
      data-testid="pages-private-from-dashboard-share-success-page-container-payplan-text"
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        bgcolor: PAYPLAN_CARD_BG,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1.25,
        }}
      >
        {renderPayPlanLogo(true)}
        <Typography
          data-testid="pages-private-from-dashboard-share-success-page-text-payplan-call-title"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#ffffff",
            lineHeight: 1.3,
          }}
        >
          You can call PayPlan on
        </Typography>
        <Typography
          data-testid="pages-private-from-dashboard-share-success-page-text-payplan-phone"
          sx={{
            fontSize: "32px",
            fontWeight: 700,
            color: PAYPLAN_ACCENT,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          {PAYPLAN_PHONE}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: "#b7c4c4",
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: "13px", color: "#b7c4c4" }}>
            {PAYPLAN_HOURS}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.18)" }} />

      {renderPayPlanInfoRows([
        {
          icon: <MailOutlineIcon sx={{ fontSize: 22 }} />,
          text: customerMobileNumber
            ? `PayPlan will connect with you on ${customerMobileNumber} at the time you chose.`
            : "PayPlan will connect with you at the time you chose.",
        },
        {
          icon: <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />,
          text: (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                No need to explain everything again
              </Box>{" "}
              — PayPlan already has your budget from IE Hub.
            </>
          ),
        },
        {
          icon: <AccessTimeIcon sx={{ fontSize: 22 }} />,
          text: (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                Anything changed?
              </Box>{" "}
              Just let PayPlan know when they contact you.
            </>
          ),
        },
      ])}
    </Paper>
  );

  const renderPayPlanEmailCard = () => (
    <Paper
      data-testid="pages-private-from-dashboard-share-success-page-container-payplan-email"
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        bgcolor: PAYPLAN_CARD_BG,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1.5,
        }}
      >
        {renderPayPlanLogo(true)}
        <Typography
          data-testid="pages-private-from-dashboard-share-success-page-text-payplan-email-title"
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#ffffff",
            lineHeight: 1.3,
            px: 1,
          }}
        >
          PayPlan will send you an email to arrange your free advice
        </Typography>
      </Box>

      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.18)" }} />

      {renderPayPlanInfoRows([
        {
          icon: <MailOutlineIcon sx={{ fontSize: 22 }} />,
          text: "PayPlan will usually connect with you within 24 hours.",
        },
        {
          icon: <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />,
          text: (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                No need to explain everything again
              </Box>{" "}
              — PayPlan already has your budget from IE Hub.
            </>
          ),
        },
        {
          icon: <AccessTimeIcon sx={{ fontSize: 22 }} />,
          text: (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                Anything changed?
              </Box>{" "}
              Just let PayPlan know when they contact you.
            </>
          ),
        },
      ])}
    </Paper>
  );

  const renderPayPlanWhatsAppCard = () => (
    <Paper
      data-testid="pages-private-from-dashboard-share-success-page-container-payplan-whatsapp"
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        bgcolor: PAYPLAN_CARD_BG,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1.5,
        }}
      >
        {renderPayPlanLogo(true)}
        <Typography
          data-testid="pages-private-from-dashboard-share-success-page-text-payplan-whatsapp-title"
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#ffffff",
            lineHeight: 1.3,
            px: 1,
          }}
        >
          PayPlan will send you a WhatsApp to discuss your options
        </Typography>
      </Box>

      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.18)" }} />

      {renderPayPlanInfoRows([
        {
          icon: <MailOutlineIcon sx={{ fontSize: 22 }} />,
          text: "PayPlan will usually connect with you within 24 hours.",
        },
        {
          icon: <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />,
          text: (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                No need to explain everything again
              </Box>{" "}
              — PayPlan already has your budget from IE Hub.
            </>
          ),
        },
        {
          icon: <AccessTimeIcon sx={{ fontSize: 22 }} />,
          text: (
            <>
              <Box component="span" sx={{ fontWeight: 700 }}>
                Anything changed?
              </Box>{" "}
              Just let PayPlan know when they contact you.
            </>
          ),
        },
      ])}
    </Paper>
  );

  const renderPayPlanCards = () => {
    if (!preferencesLoaded || payPlanVariants.length === 0) {
      return null;
    }

    return (
      <Box
        data-testid="pages-private-from-dashboard-share-success-page-container-payplan-cards"
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {payPlanVariants.includes("promo") && renderPayPlanPromoCard()}
        {payPlanVariants.includes("text") && renderPayPlanTextCard()}
        {payPlanVariants.includes("email") && renderPayPlanEmailCard()}
        {payPlanVariants.includes("whatsapp") && renderPayPlanWhatsAppCard()}
      </Box>
    );
  };

  const renderAddCompanyDialog = () => {
    return (
      <Dialog data-testid="pages-private-from-dashboard-render-add-company-dialog-modal-action"
        open={addCompanyOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              maxHeight: "90vh",
              padding: "0px 0px 20px 0px",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <AddCompanyDialog
          onClose={() => {
            setAddCompanyOpen(false);
          }}
          balanceAmount={summary.balanceAmount}
          ieMstrLenderData={ieMstrLenderData}
          digitalLenders={digitalLenders}
          freepostCreditorNamesData={freepostCreditorNamesData}
          declarationData={declarationData}
          source="success"
          companies={companies}
        />
      </Dialog>
    );
  };

  return (
    <>
      {renderAddCompanyDialog()}

      {/* Mobile View */}
      <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-1"
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#fafafa",
        }}
      >
        <Header data-testid="pages-private-from-dashboard-share-success-page-header-action-1" />

        <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-2"
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pt: 11,
            pb: 18,
          }}
        >
          {/* Success Icon and Message */}
          <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-3"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-4"
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor:
                  sharedCompanies.length > 0 && !isShared
                    ? "#dcfce7"
                    : "#ff5e0036",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {sharedCompanies.length > 0 && !isShared ? (
                <CheckCircleIcon sx={{ fontSize: 32, color: "#16a34a" }} />
              ) : (
                <InfoIcon sx={{ fontSize: 32, color: "#FF5C00" }} />
              )}
            </Box>

            <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-5" sx={{ textAlign: "center" }}>
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-1"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: "22px",
                  color: "#003b3e",
                  mb: 1,
                }}
              >
                {isShared
                  ? "You’ve Already Shared Your Budget Today"
                  : "Success!"}
              </Typography>
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-2"
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#00243e",
                  opacity: 0.8,
                }}
              >
                {isShared
                  ? "It looks like you’ve already shared this budget form, and no changes have been made. To avoid confusion, the same budget can only be shared once per calendar day."
                  : sharedCompanies.length > 0
                    ? `Your budget has been successfully shared with ${sharedCompanies.length} ${sharedCompanies.length > 1 ? "companies" : "company"}`
                    : `Your budget has been saved in your account.`}
              </Typography>
            </Box>
          </Box>

          {sharedCompanies.length > 0 && !isShared && (
            <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-6"
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid #e4e7eb",
              }}
            >
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-shared-with-companies-1"
                variant="h6"
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 2,
                }}
              >
                Shared with companies
              </Typography>

              {sharedCompanies.map((company, index) => (
                <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-7"
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: index < sharedCompanies.length - 1 ? 2 : 0,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 22, color: "#003d40" }} />
                  <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-3"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#00243e",
                    }}
                  >
                    {companyDisplayName(company)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {isMobile && renderPayPlanCards()}

          {/* Benefits Check Promotional Card */}
          <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-8"
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #155f54",
              background:
                "linear-gradient(284.97deg, #018374 1.26%, #004D44 99.98%)",
              boxShadow: "inset 0px 0px 10.9px rgba(119, 172, 255, 0.2)",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-9" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-complete-a-benefits-check-1"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "white",
                  lineHeight: "28px",
                }}
              >
                Complete a Benefits Check
              </Typography>
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-4"
                sx={{
                  fontSize: "16px",
                  color: "#e1e5e5",
                  lineHeight: "normal",
                }}
              >
                Based on the information you have provided, we estimate that you
                could be entitled to an extra{" "}
                <Box data-testid="pages-private-from-dashboard-share-success-page-container-£-1" component="span" sx={{ fontWeight: 600 }}>
                  £{benefitsAmount}
                </Box>
                . Use our tool to find out exactly how much more in benefits you
                could claim.
              </Typography>
            </Box>

            <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-start-benefits-check-1"
              onClick={handleStartBenefitsCheck}
              sx={{
                bgcolor: "white",
                color: "#155f54",
                borderRadius: "12px",
                py: 1,
                px: 1.5,
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "none",
                alignSelf: "flex-start",
                "&:hover": {
                  bgcolor: "#f2f5f6",
                },
              }}
            >
              Start benefits check
            </Button>
          </Paper>

          {/* Share with Another Company */}
          <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-10"
            sx={{
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #155f54",
              bgcolor: "#f2f5f6",
            }}
          >
            <Typography data-testid="pages-private-from-dashboard-share-success-page-text-share-with-another-company-1"
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#00243e",
                mb: 0.5,
              }}
            >
              Share with another company
            </Typography>
            <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-5"
              sx={{
                fontSize: "14px",
                color: "#6b7a7b",
                mb: 3,
              }}
            >
              You can send your budget to any other companies that are on our
              sharing list.
            </Typography>

            <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-share-with-company-1"
              fullWidth
              variant="contained"
              startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
              onClick={handleShareWithCompany}
              sx={{
                bgcolor: "#155f54",
                color: "white",
                borderRadius: "8px",
                py: 1.25,
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#0d4a42",
                },
              }}
            >
              Share with Company
            </Button>
          </Paper>

          {/* Can't See Your Company */}
          <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-11"
            sx={{
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #155f54",
              bgcolor: "#f8f8f8",
            }}
          >
            <Typography data-testid="pages-private-from-dashboard-share-success-page-text-cant-see-your-company-on-our-share-list?-1"
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#00243e",
                mb: 0.5,
              }}
            >
              Can't see your company on our share list?
            </Typography>
            <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-6"
              sx={{
                fontSize: "14px",
                color: "#6b7a7b",
                mb: 3,
              }}
            >
              There are some companies we're not able to share with directly,
              but we may still be able to help. We offer free posting to a
              number of other companies, just click below to search and see if
              yours is included.
            </Typography>

            <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-search-companies-1"
              fullWidth
              variant="outlined"
              startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
              onClick={handleSearchCompanies}
              sx={{
                bgcolor: "white",
                color: "#155f54",
                borderColor: "#155f54",
                borderRadius: "8px",
                py: 1.25,
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#f2f5f6",
                  borderColor: "#155f54",
                },
              }}
            >
              Search Companies
            </Button>
          </Paper>

          {/* Finished Sharing */}
          <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-12"
            sx={{
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #c7c7c7",
              bgcolor: "#f8f8f8",
            }}
          >
            <Typography data-testid="pages-private-from-dashboard-share-success-page-text-finished-sharing?-1"
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#00243e",
                mb: 0.5,
              }}
            >
              Finished sharing?
            </Typography>
            <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-7"
              sx={{
                fontSize: "14px",
                color: "#6b7a7b",
                mb: 3,
              }}
            >
              Visit your dashboard, to see next steps, recommendations and
              anything else we can help you with.
            </Typography>

            <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-go-to-dashboard-1"
              fullWidth
              variant="outlined"
              startIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              onClick={handleGoToDashboard}
              sx={{
                bgcolor: "white",
                color: "#155f54",
                borderColor: "#929292",
                borderWidth: "0.5px",
                borderRadius: "8px",
                py: 1.25,
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#f2f5f6",
                  borderColor: "#929292",
                },
              }}
            >
              Go to Dashboard
            </Button>
          </Paper>
        </Box>

        <BottomTabBar data-testid="pages-private-from-dashboard-share-success-page-nav-bar-action" />
      </Box>

      {/* Desktop View */}
      <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-13"
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "#fafafa",
        }}
      >
        <Header data-testid="pages-private-from-dashboard-share-success-page-header-action-2" />

        <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-14"
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            py: 4,
          }}
        >
          <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-15"
            sx={{
              width: "100%",
              maxWidth: "742px",
              px: 2,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Success Icon and Message */}
            <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-16"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
                maxWidth: "500px",
                mx: "auto",
              }}
            >
              <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-17"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor:
                    sharedCompanies.length && !isShared > 0
                      ? "#dcfce7"
                      : "#ff5e0036",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {sharedCompanies.length > 0 && !isShared ? (
                  <CheckCircleIcon sx={{ fontSize: 32, color: "#16a34a" }} />
                ) : (
                  <InfoIcon sx={{ fontSize: 32, color: "#FF5C00" }} />
                )}
              </Box>

              <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-18" sx={{ textAlign: "center" }}>
                <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-8"
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    fontSize: "22px",
                    color: "#003b3e",
                    mb: 1,
                  }}
                >
                  {isShared
                    ? "You’ve Already Shared Your Budget Today"
                    : "Success!"}
                </Typography>
                <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-9"
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    color: "#00243e",
                    opacity: 0.8,
                  }}
                >
                  {isShared
                    ? "It looks like you’ve already shared this budget form, and no changes have been made. To avoid confusion, the same budget can only be shared once per calendar day."
                    : sharedCompanies.length > 0
                      ? `Your budget has been successfully shared with ${sharedCompanies.length} ${sharedCompanies.length > 1 ? "companies" : "company"}`
                      : `Your budget has been saved in your account.`}
                </Typography>
              </Box>
            </Box>

            {sharedCompanies.length > 0 && !isShared && (
              <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-19"
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid #e4e7eb",
                }}
              >
                <Typography data-testid="pages-private-from-dashboard-share-success-page-text-shared-with-companies-2"
                  variant="h6"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#00243e",
                    mb: 2,
                  }}
                >
                  Shared with companies
                </Typography>

                {sharedCompanies.map((company, index) => (
                  <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-20"
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: index < sharedCompanies.length - 1 ? 2 : 0,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 22, color: "#003d40" }} />
                    <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-10"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#00243e",
                      }}
                    >
                      {companyDisplayName(company)}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            )}

            {!isMobile && renderPayPlanCards()}

            {/* Benefits Check Promotional Card */}
            <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-21"
              sx={{
                p: 3,
                borderRadius: "12px",
                border: "1px solid #155f54",
                background:
                  "linear-gradient(284.97deg, #018374 1.26%, #004D44 99.98%)",
                boxShadow: "inset 0px 0px 10.9px rgba(119, 172, 255, 0.2)",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Box data-testid="pages-private-from-dashboard-share-success-page-container-action-22" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography data-testid="pages-private-from-dashboard-share-success-page-text-complete-a-benefits-check-2"
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "white",
                    lineHeight: "28px",
                  }}
                >
                  Complete a Benefits Check
                </Typography>
                <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-11"
                  sx={{
                    fontSize: "16px",
                    color: "#e1e5e5",
                    lineHeight: "normal",
                  }}
                >
                  Based on the information you have provided, we estimate that
                  you could be entitled to an extra{" "}
                  <Box data-testid="pages-private-from-dashboard-share-success-page-container-£-2" component="span" sx={{ fontWeight: 600 }}>
                    £{benefitsAmount}
                  </Box>
                  . Use our tool to find out exactly how much more in benefits
                  you could claim.
                </Typography>
              </Box>

              <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-start-benefits-check-2"
                onClick={handleStartBenefitsCheck}
                sx={{
                  bgcolor: "white",
                  color: "#155f54",
                  borderRadius: "12px",
                  py: 1,
                  px: 1.5,
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  alignSelf: "flex-start",
                  "&:hover": {
                    bgcolor: "#f2f5f6",
                  },
                }}
              >
                Start benefits check
              </Button>
            </Paper>

            {/* Share with Another Company */}
            <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-23"
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid #155f54",
                bgcolor: "#f2f5f6",
              }}
            >
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-share-with-another-company-2"
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 0.5,
                }}
              >
                Share with another company
              </Typography>
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-12"
                sx={{
                  fontSize: "14px",
                  color: "#6b7a7b",
                  mb: 3,
                }}
              >
                You can send your budget to any other companies that are on our
                sharing list.
              </Typography>

              <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-share-with-company-2"
                variant="contained"
                startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
                onClick={handleShareWithCompany}
                sx={{
                  bgcolor: "#155f54",
                  color: "white",
                  borderRadius: "8px",
                  py: 1.25,
                  px: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#0d4a42",
                  },
                }}
              >
                Share with Company
              </Button>
            </Paper>

            {/* Can't See Your Company */}
            <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-24"
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid #c7c7c7",
                bgcolor: "#f8f8f8",
              }}
            >
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-cant-see-your-company-on-our-share-list?-2"
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 0.5,
                }}
              >
                Can't see your company on our share list?
              </Typography>
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-13"
                sx={{
                  fontSize: "14px",
                  color: "#6b7a7b",
                  mb: 3,
                }}
              >
                There are some companies we're not able to share with directly,
                but we may still be able to help. We offer free posting to a
                number of other companies, just click below to search and see if
                yours is included.
              </Typography>

              <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-search-companies-2"
                variant="outlined"
                startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                onClick={handleSearchCompanies}
                sx={{
                  bgcolor: "white",
                  color: "#155f54",
                  borderColor: "#155f54",
                  borderRadius: "8px",
                  py: 1.25,
                  px: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#f2f5f6",
                    borderColor: "#155f54",
                  },
                }}
              >
                Search Companies
              </Button>
            </Paper>

            {/* Finished Sharing */}
            <Paper data-testid="pages-private-from-dashboard-share-success-page-container-action-25"
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid #c7c7c7",
                bgcolor: "#f8f8f8",
              }}
            >
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-finished-sharing?-2"
                variant="h6"
                sx={{
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#00243e",
                  mb: 0.5,
                }}
              >
                Finished sharing?
              </Typography>
              <Typography data-testid="pages-private-from-dashboard-share-success-page-text-action-14"
                sx={{
                  fontSize: "14px",
                  color: "#6b7a7b",
                  mb: 3,
                }}
              >
                Visit your dashboard, to see next steps, recommendations and
                anything else we can help you with.
              </Typography>

              <Button data-testid="pages-private-from-dashboard-share-success-page-btn-handle-go-to-dashboard-2"
                variant="outlined"
                startIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                onClick={handleGoToDashboard}
                sx={{
                  bgcolor: "white",
                  color: "#155f54",
                  borderColor: "#929292",
                  borderRadius: "8px",
                  py: 1.25,
                  px: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#f2f5f6",
                    borderColor: "#929292",
                  },
                }}
              >
                Go to Dashboard
              </Button>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ShareSuccessPage;
